import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import React, { ComponentProps, MouseEventHandler, ReactElement, useCallback, useMemo } from 'react'
import SearchTileRowInclusionItem from './SearchTileRowInclusionItem'
import LabeledSheet from 'components/Luxkit/LabeledSheet'
import { partitionBy, take } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LuxPlusLabel from 'luxPlus/components/LuxPlusLabel'

const HighlightedInclusionsContainer = styled(Group)`
  padding: ${rem(8)};
`

type InheritedSearchTileRowInclusionItemProps = Pick<ComponentProps<typeof SearchTileRowInclusionItem>, 'lineClamp' | 'isHighlighted'>

interface Props extends InheritedSearchTileRowInclusionItemProps {
  inclusionsTitle?: string | ReactElement
  inclusions: Array<App.OfferInclusion>
  /**
   * The number of items to display before the rest are truncated.
   *
   * If set to zero, nothing will be displayed.
   */
  truncateAfter?: number
  onShowMoreClick: () => void
  showSelectPackageOnlyLabel?: boolean
}

function SearchTileStackInclusionsList({
  lineClamp,
  isHighlighted,
  inclusionsTitle,
  inclusions,
  truncateAfter,
  onShowMoreClick,
  showSelectPackageOnlyLabel,
}: Props) {
  const [flashInclusionItems, nonFlashInclusionItems] = useMemo(() => partitionBy(inclusions, inclusion => !!inclusion.isFlash), [inclusions])
  const [bonusInclusionItems, inclusionItems] = useMemo(() => partitionBy(nonFlashInclusionItems, inclusion => !!inclusion.isLuxPlusBonus), [nonFlashInclusionItems])
  const regularInclusionsTruncateLength = !truncateAfter || (flashInclusionItems.length + bonusInclusionItems.length) >= (truncateAfter ?? 0) ? 0 : truncateAfter - (flashInclusionItems.length + bonusInclusionItems.length)
  const flashInclusionsTruncateLength = !bonusInclusionItems.length || !truncateAfter ? (truncateAfter ?? 0) : Math.max((truncateAfter - bonusInclusionItems.length), 0)
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(regularInclusionsTruncateLength, inclusionItems)
  const [alwaysVisibleFlashInclusions, collapsibleFlashInclusions] = useListTruncator(flashInclusionsTruncateLength, flashInclusionItems)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    onShowMoreClick()
  }, [onShowMoreClick])

  if (!alwaysVisibleInclusions.length && !flashInclusionItems.length && !bonusInclusionItems.length) return null

  return <Group direction="vertical" gap={4}>
    {!!inclusionsTitle && <BodyText variant="medium" weight="bold">{inclusionsTitle} {showSelectPackageOnlyLabel && <UrgencyLabel variant="positive">Select packages only</UrgencyLabel>}</BodyText>}
    {!!bonusInclusionItems.length && truncateAfter && <LabeledSheet kind="lux-plus" label={<LuxPlusLabel type="bonus-inclusions" />}>
      <HighlightedInclusionsContainer direction="vertical" gap={4}>
        {take(bonusInclusionItems, truncateAfter).map((inclusion) => <SearchTileRowInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          lineClamp={lineClamp}
        />)}
      </HighlightedInclusionsContainer>
    </LabeledSheet>}
    {!!flashInclusionItems.length && !bonusInclusionItems.length && truncateAfter && <LabeledSheet kind="exclusive" label="Limited time inclusions">
      <HighlightedInclusionsContainer direction="vertical" gap={4}>
        {take(flashInclusionItems, truncateAfter).map((inclusion) => <SearchTileRowInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          lineClamp={lineClamp}
        />)}
      </HighlightedInclusionsContainer>
    </LabeledSheet>}
    {!!flashInclusionItems.length && !!bonusInclusionItems.length &&
      alwaysVisibleFlashInclusions.map((inclusion) => <SearchTileRowInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        isHighlighted={false}
        lineClamp={lineClamp}
      />)
    }
    { alwaysVisibleInclusions.map((inclusion) => <SearchTileRowInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      isHighlighted={isHighlighted}
      lineClamp={lineClamp}
    />)}
    {!!collapsibleInclusions.length && <TextLink size="medium" onClick={handleMoreClick}>+{collapsibleInclusions.length + collapsibleFlashInclusions.length} more</TextLink>}
  </Group>
}

export default SearchTileStackInclusionsList
